import { useState } from 'react';
import type { ModerationRecord } from '../net/api';

const when = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });

/** A player's own warnings and bans with an appeal form for each. Shared by the Account panel and the signed-out appeal page. */
export function RecordView({ record, onAppeal }: { record: ModerationRecord; onAppeal: (type: 'warning' | 'ban', id: number, reason: string) => Promise<void> }) {
  const [open, setOpen] = useState<{ type: 'warning' | 'ban'; id: number }>();
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const appealOf = (type: string, id: number) => record.appeals.find((a) => a.targetType === type && a.targetId === id);

  async function send() {
    if (!open) return;
    try {
      await onAppeal(open.type, open.id, reason);
      setMsg({ kind: 'ok', text: "Your appeal was sent. Our team will review it. You'll see the decision here." });
      setOpen(undefined);
      setReason('');
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  }

  const row = (type: 'warning' | 'ban', id: number, title: string, detail: string, active: boolean) => {
    const a = appealOf(type, id);
    return (
      <div key={`${type}${id}`} className="record-row">
        <div className="stack tight">
          <strong>{title}</strong>
          <span className="muted small-print">{detail}</span>
          {a && <span className="small-print">Appeal: {a.status === 'open' ? 'waiting for review' : a.status}{a.decisionNotes ? ` · “${a.decisionNotes}”` : ''}</span>}
        </div>
        {!a && active && <button className="btn small ghost" onClick={() => setOpen({ type, id })}>Appeal</button>}
      </div>
    );
  };

  return (
    <div className="stack">
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      {record.bans.length === 0 && record.warnings.length === 0 && <p className="muted">Your record is clean. Thanks for being kind!</p>}
      {record.bans.map((b) =>
        row('ban', b.id, b.type === 'permanent' ? 'Permanent ban' : `Ban until ${when(b.endsAt!)}`, `${b.reason} · from ${when(b.startsAt)}${b.liftedAt ? ' · lifted' : ''}`, !b.liftedAt),
      )}
      {record.warnings.map((w) => row('warning', w.id, `Warning: ${w.rule}`, `${w.reason} · ${when(w.createdAt)} · ${w.status}`, w.status === 'active'))}
      {open && (
        <form className="report-form" onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <label htmlFor="appeal-reason">Why should we look at this again?</label>
          <textarea id="appeal-reason" rows={3} minLength={20} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
          <p className="muted small-print">Each warning or ban can be appealed once. Appealing doesn't pause a ban.</p>
          <div className="row-wrap">
            <button type="button" className="btn ghost" onClick={() => setOpen(undefined)}>Cancel</button>
            <button className="btn" disabled={reason.trim().length < 20}>Send appeal</button>
          </div>
        </form>
      )}
    </div>
  );
}
